import { useState } from 'react';
import { getApiUrl } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

interface NamePromptModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}

export default function NamePromptModal({ open, onClose, onSubmit }: NamePromptModalProps) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    const trimmedName = name.trim();

    try {
      if (showAdmin && password) {
        const res = await fetch(getApiUrl('/api/admin/ratings'), {
          headers: { 'x-admin-key': password },
        });
        if (res.ok) {
          localStorage.setItem('jointRaterSession', JSON.stringify({ name: trimmedName, isAdmin: true, adminKey: password }));
          localStorage.setItem('rollerName', trimmedName);
          onSubmit(trimmedName);
          return;
        } else {
          setAdminError('Invalid admin password');
          setSubmitting(false);
          return;
        }
      }

      localStorage.setItem('jointRaterSession', JSON.stringify({ name: trimmedName }));
      localStorage.setItem('rollerName', trimmedName);
      onSubmit(trimmedName);
    } catch {
      setAdminError('Something went wrong');
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={e => e.stopPropagation()}
            className="bg-cannabis-800 border border-cannabis-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-4"
          >
            <div className="text-center">
              <h2 className="text-xl font-bold text-cream">What's your name?</h2>
              <p className="text-cream/40 text-xs mt-1">Required to rate, comment, or battle.</p>
            </div>

            <input
              type="text"
              placeholder="Enter your name..."
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              autoFocus
              className="w-full bg-cannabis-900 border border-cannabis-600 rounded-xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400"
            />

            {showAdmin && (
              <>
                <input
                  type="password"
                  placeholder="Admin password..."
                  value={password}
                  onChange={e => { setPassword(e.target.value); setAdminError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  className="w-full bg-cannabis-900 border border-cannabis-600 rounded-xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400"
                />
                {adminError && <p className="text-red-400 text-sm text-center">{adminError}</p>}
              </>
            )}

            <button
              onClick={handleSubmit}
              disabled={!name.trim() || submitting}
              className="w-full py-3 bg-[#4ade80]/15 border border-[#4ade80]/30 text-[#4ade80] font-semibold text-sm rounded-xl hover:bg-[#4ade80]/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? 'Signing in...' : 'Continue'}
            </button>

            <div className="flex justify-between items-center">
              <button
                onClick={() => setShowAdmin(!showAdmin)}
                className="text-cream/30 text-xs hover:text-cream/50 transition-colors"
              >
                {showAdmin ? 'Hide admin' : 'Admin login'}
              </button>
              <button
                onClick={onClose}
                className="text-cream/30 text-xs hover:text-cream/50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
